import React, { useEffect, useState } from 'react';
import { useAppSelector } from '@/app/lib/hooks';
import { fetchExchangeRate } from '@/utils/currencyUtils';
import { useTranslation } from 'react-i18next';

interface ConvertedPriceProps {
  price: number; // Price in EUR
  className?:string;
}

const ConvertedPrice: React.FC<ConvertedPriceProps> = ({ price }) => {
  const selectedCurrency = useAppSelector(state => state.currencies.selectedCurrency);
  const currencies = useAppSelector(state => state.currencies.currencies);
  const [convertedPrice, setConvertedPrice] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { t } = useTranslation("guydeez");

  useEffect(() => {
    const convertPrice = async () => {
      // Ensure price is a valid number
      const numericPrice = Number(price);
      if (isNaN(numericPrice)) {
        // Silently fall back to 0 — a transient `undefined` during the
        // first render is benign and would otherwise flag the page as
        // "browser errors logged to console" in Lighthouse Best Practices.
        setConvertedPrice(0);
        setLoading(false);
        return;
      }

      if (selectedCurrency && selectedCurrency !== 'EUR') {
        setLoading(true);
        const rate = await fetchExchangeRate('EUR', selectedCurrency);
        if (rate) {
          setConvertedPrice(numericPrice * rate);
        } else {
          setConvertedPrice(null);
        }
        setLoading(false);
      } else {
        setConvertedPrice(numericPrice); // Default to EUR if no conversion needed
        setLoading(false);
      }
    };

    convertPrice();
  }, [price, selectedCurrency]);

  const currencySymbol = currencies.find(currency => currency.code === selectedCurrency)?.symbol || '€';

  return (
    <span className="flex items-center">
      {loading ? (
        t('loading')
      ) : (
        <>
          {convertedPrice?.toFixed(2)}
          <span className="ml-1">{currencySymbol}</span> {/* Ensures consistent alignment */}
        </>
      )}
    </span>
  );
};

export default ConvertedPrice;
